import React, { useState } from 'react';
import { Upload, FileSpreadsheet, Database, CheckCircle, AlertCircle } from 'lucide-react';

interface UploadedFile {
  name: string;
  type: string;
  recordCount: number;
  uploadDate: string;
  status: 'success' | 'error';
}

export function ImportView() {
  const [taxFiles, setTaxFiles] = useState<UploadedFile[]>([
    {
      name: 'florida_tax_delinquent_nov2025.csv',
      type: 'Tax Delinquent',
      recordCount: 847,
      uploadDate: 'Dec 1, 2025',
      status: 'success'
    }
  ]);

  const [regularFiles, setRegularFiles] = useState<UploadedFile[]>([
    {
      name: 'foreclosure_georgia_leads.xlsx',
      type: 'Pre-Foreclosure',
      recordCount: 412,
      uploadDate: 'Nov 28, 2025',
      status: 'success'
    }
  ]);

  const handleTaxFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('Uploading tax delinquent file:', files[0].name);
      alert(`Tax Delinquent File Upload\n\nFile: ${files[0].name}\nType: ${files[0].type}\n\nThis will be connected to backend API for processing.`);
    }
  };

  const handleRegularFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('Uploading regular list file:', files[0].name);
      alert(`Regular List File Upload\n\nFile: ${files[0].name}\nType: ${files[0].type}\n\nThis will be connected to backend API for processing.`);
    }
  };

  const handleTaxDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-red-400', 'bg-red-50');
  };

  const handleTaxDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('border-red-400', 'bg-red-50');
  };

  const handleTaxDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-red-400', 'bg-red-50');
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log('Dropped tax delinquent file:', files[0].name);
      alert(`Tax Delinquent File Upload\n\nFile: ${files[0].name}\nType: ${files[0].type}\n\nThis will be connected to backend API for processing.`);
    }
  };

  const handleRegularDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
  };

  const handleRegularDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
  };

  const handleRegularDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log('Dropped regular list file:', files[0].name);
      alert(`Regular List File Upload\n\nFile: ${files[0].name}\nType: ${files[0].type}\n\nThis will be connected to backend API for processing.`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary">Import Lists</h1>
          <p className="text-gray-600 mt-1">Upload CSV or Excel files to import lead lists</p>
        </div>
      </div>

      {/* Tax Delinquent Upload Section */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-lg p-8 border-2 border-red-200">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
            <FileSpreadsheet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-tdb-primary">Tax Delinquent Lists</h2>
            <p className="text-sm text-gray-600">Upload tax delinquent property lists (CSV or Excel)</p>
          </div>
        </div>

        {/* Upload Area */}
        <div
          className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 text-center transition-all hover:border-red-400"
          onDragOver={handleTaxDragOver}
          onDragLeave={handleTaxDragLeave}
          onDrop={handleTaxDrop}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-700">Drag and drop your file here</p>
              <p className="text-sm text-gray-500 mt-1">or click to browse</p>
            </div>
            <input
              type="file"
              id="tax-file-upload"
              accept=".csv,.xlsx,.xls"
              onChange={handleTaxFileUpload}
              className="hidden"
            />
            <label
              htmlFor="tax-file-upload"
              className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 cursor-pointer transition-all"
            >
              Choose File
            </label>
            <p className="text-xs text-gray-500">Supported formats: CSV, XLSX, XLS</p>
          </div>
        </div>

        {/* Upload History */}
        <div className="mt-6 bg-white rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Recent Uploads</h3>
          <div className="space-y-2">
            {taxFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {file.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <div className="font-semibold text-gray-800">{file.name}</div>
                    <div className="text-sm text-gray-600">{file.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">{file.uploadDate}</div>
                  <div className="font-bold text-red-600">{file.recordCount} records</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Regular Lists Upload Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 border-2 border-blue-200">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
            <Database className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-tdb-primary">Regular Lists</h2>
            <p className="text-sm text-gray-600">Upload other lead lists (foreclosure, probate, etc.)</p>
          </div>
        </div>

        {/* Upload Area */}
        <div
          className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 text-center transition-all hover:border-blue-400"
          onDragOver={handleRegularDragOver}
          onDragLeave={handleRegularDragLeave}
          onDrop={handleRegularDrop}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-500" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-700">Drag and drop your file here</p>
              <p className="text-sm text-gray-500 mt-1">or click to browse</p>
            </div>
            <input
              type="file"
              id="regular-file-upload"
              accept=".csv,.xlsx,.xls"
              onChange={handleRegularFileUpload}
              className="hidden"
            />
            <label
              htmlFor="regular-file-upload"
              className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 cursor-pointer transition-all"
            >
              Choose File
            </label>
            <p className="text-xs text-gray-500">Supported formats: CSV, XLSX, XLS</p>
          </div>
        </div>

        {/* Upload History */}
        <div className="mt-6 bg-white rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Recent Uploads</h3>
          <div className="space-y-2">
            {regularFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {file.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <div className="font-semibold text-gray-800">{file.name}</div>
                    <div className="text-sm text-gray-600">{file.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">{file.uploadDate}</div>
                  <div className="font-bold text-blue-600">{file.recordCount} records</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Guidelines */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-tdb-primary mb-4">File Format Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Tax Delinquent Lists</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Property Address (required)</li>
              <li>• Owner Name</li>
              <li>• Tax Amount Owed</li>
              <li>• Property Value</li>
              <li>• County/State</li>
              <li>• Phone/Email (if available)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Regular Lists</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Property Address (required)</li>
              <li>• Owner Name (required)</li>
              <li>• List Type (foreclosure, probate, etc.)</li>
              <li>• Equity Amount</li>
              <li>• Owner Status</li>
              <li>• Contact Information</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
